/**
 * Zustand store for the pricing system's runtime UI state.
 *
 * Manages the upgrade prompt (visible/hidden, message content) and provides a
 * registration point for the App-level pricing-page navigation callback, so
 * the UpgradeToast can open the pricing page without prop drilling.
 */

import { create } from 'zustand';
import { getUpgradeMessage, UpgradeMessageKey } from './upgradeMessages';

interface PricingStoreState {
    /** Whether the upgrade prompt is currently visible. */
    upgradePromptVisible: boolean;

    /** The message displayed inside the upgrade prompt. */
    upgradeMessage: string;

    /**
     * Shows the upgrade prompt for a named message key.
     * The message text is resolved from the central message registry.
     * Auto-dismisses after 5 seconds.
     */
    showUpgradePrompt: (key: UpgradeMessageKey, params?: Record<string, string>) => void;

    /** Immediately hides the upgrade prompt and cancels any pending timer. */
    hideUpgradePrompt: () => void;

    /**
     * Callback that opens the pricing page.
     * Registered by App.tsx on mount via `registerPricingNavigation`.
     * Null until App.tsx has mounted and registered it.
     */
    onNavigateToPricing: (() => void) | null;

    /** Called once by App.tsx to wire up the pricing navigation action. */
    registerPricingNavigation: (fn: () => void) => void;
}

/** Timer reference kept outside the store to avoid stale closure issues. */
let dismissTimer: ReturnType<typeof setTimeout> | null = null;

const UPGRADE_PROMPT_DURATION_MS = 5000;

export const usePricingStore = create<PricingStoreState>((set) => ({
    upgradePromptVisible: false,
    upgradeMessage: '',
    onNavigateToPricing: null,

    showUpgradePrompt: (key: UpgradeMessageKey, params?: Record<string, string>) => {
        // Clear any active timer before starting a new one.
        if (dismissTimer) clearTimeout(dismissTimer);

        const message = getUpgradeMessage(key, params);
        set({ upgradePromptVisible: true, upgradeMessage: message });

        dismissTimer = setTimeout(() => {
            set({ upgradePromptVisible: false });
        }, UPGRADE_PROMPT_DURATION_MS);
    },

    hideUpgradePrompt: () => {
        if (dismissTimer) clearTimeout(dismissTimer);
        set({ upgradePromptVisible: false });
    },

    registerPricingNavigation: (fn: () => void) => {
        set({ onNavigateToPricing: fn });
    },
}));
